import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@base-app';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants/common';
import { StyledHighlihtedValue, StyledLabel, StyledValue } from '@modules/study-insights/components/styles';
import { SequenceGoalProps } from './SequenceGoal.types';
import { StyledSequenceGoalContainer, StyledSequenceGoalDetail } from './SequenceGoal.styles';
import { ColorGoals, SequenceGoalChartData } from './SequenceGoalChart/SequenceGoalChart.types';
import { colors } from '@common/helpers';

import SequenceGoalChart from './SequenceGoalChart/SequenceGoalChart';

const SequenceGoal: FC<SequenceGoalProps> = ({
  accentColor,
  date,
  dateLabel,
  id,
  percentage,
  target,
  targetLabel,
  value,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [colorGoals, setColorGoals] = useState<ColorGoals>({ sector: colors.white, text: colors.white });

  const chartData: SequenceGoalChartData[] = [
    { name: 'Remaining', value: target - (value ?? 0) },
    { name: 'Achieved', value: value ?? 0 },
  ];

  const getColors = (percent: number): void => {
    if (percent <= 25) {
      setColorGoals({
        sector: colors.yellow300,
        text: '#C69806',
      });
    }
    if (percent > 25) {
      setColorGoals({
        sector: '#AFC646',
        text: '#7D9427',
      });
    }
    if (percent > 50) {
      setColorGoals({
        sector: '#8FC960',
        text: '#497A44',
      });
    }
    if (percent > 75) {
      setColorGoals({
        sector: '#497A44',
        text: '#497A44',
      });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line  @typescript-eslint/prefer-nullish-coalescing
    if (percentage || percentage === 0) {
      getColors(percentage);
    }
  }, [percentage]);

  return (
    <StyledSequenceGoalContainer data-testid={`${id}-sequence-goal`}>
      <Box css={{ marginRight: '10%' }}>
        <SequenceGoalChart data={chartData} accentColor={accentColor} color={colorGoals} />
      </Box>
      <StyledSequenceGoalDetail>
        <StyledSequenceGoalDetail>
          <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: '$3' }}>
            <StyledHighlihtedValue data-testid={`${id}-sequenced-total-value`} css={{ color: colorGoals.text }}>
              {new Intl.NumberFormat().format(value as number)}
            </StyledHighlihtedValue>
            <StyledValue css={{ color: '$darkGray300' }}>{t('sequenced_samples.sequenced')}</StyledValue>
          </Box>
          <Box css={{ marginBottom: '$3' }}>
            <StyledLabel data-testid={`${id}-participants-target-label`}>{targetLabel}</StyledLabel>
            <StyledValue data-testid={`${id}-participants-target-value`}>
              {`${new Intl.NumberFormat().format(target)} ${t('participants')}`}
            </StyledValue>
          </Box>
          <Box>
            <StyledLabel data-testid={`${id}-date-label`}>{dateLabel}</StyledLabel>
            <StyledValue data-testid={`${id}-date-value`}>{date}</StyledValue>
          </Box>
        </StyledSequenceGoalDetail>
      </StyledSequenceGoalDetail>
    </StyledSequenceGoalContainer>
  );
};

export default SequenceGoal;
